import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Stack,
    Switch,
    Table,
    Typography,
} from "@mui/joy";
import { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../context";
import { APIGetAppointments, APIUpdateAppointment } from "../api";
import { CalendarMonthRounded, AddRounded, DoneAllRounded } from "@mui/icons-material";
import ModalCreateAppointment from "../components/modals/ModalCreateAppointment";


export default function AppointmentsPage() {
    const { currentDoctor, token, patients } = useContext(LayoutContext);

    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [onlyOverdue, setOnlyOverdue] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);

    const canCreate =
        currentDoctor?.type_profile?.includes('admin') ||
        currentDoctor?.type_profile?.includes('doctor') ||
        currentDoctor?.type_profile?.includes('supervisor');

    const load = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await APIGetAppointments(token, onlyOverdue ? { overdue: 'true' } : {});
            setAppointments(data.appointments || []);
        } catch (e) {
            setError(e?.response?.data?.error || 'Не удалось загрузить приёмы');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [token, onlyOverdue]);

    // Карта id → пациент (для отображения имён)
    const patientById = (id) => patients?.find(p => p.id === id)?.name || `#${id}`;

    const handleMarkResponded = async (appt) => {
        try {
            await APIUpdateAppointment(token, appt.id, { mark_responded: true });
            setAppointments(prev => prev.map(a => a.id === appt.id ? { ...a, is_responded: true, is_overdue: false } : a));
            setSuccess(`Приём #${appt.id} помечен как отвеченный`);
        } catch (e) {
            setError(e?.response?.data?.error || 'Не удалось обновить приём');
        }
    };

    return (
        <Box sx={{
            px: { xs: 2, md: 6 }, pt: 2, pb: 3,
            display: 'flex', flexDirection: 'column', gap: 2,
        }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Typography level="h2" component="h1" startDecorator={<CalendarMonthRounded color="primary" />}>
                    Приёмы пациентов
                </Typography>

                <Stack direction="row" spacing={2} alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography level="body-sm">Только просроченные</Typography>
                        <Switch checked={onlyOverdue} onChange={(e) => setOnlyOverdue(e.target.checked)} />
                    </Stack>
                    {canCreate && (
                        <Button startDecorator={<AddRounded />} onClick={() => setCreateOpen(true)}>
                            Создать приём
                        </Button>
                    )}
                </Stack>
            </Stack>

            {error && <Alert color="danger" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert color="success" onClose={() => setSuccess(null)}>{success}</Alert>}

            <Card variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : appointments.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="neutral">
                            {onlyOverdue ? 'Нет просроченных приёмов' : 'Приёмов пока нет'}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table hoverRow sx={{ '--TableCell-paddingY': '0.7rem' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 60 }}>ID</th>
                                    <th>Пациент</th>
                                    <th>Дата приёма</th>
                                    <th>Дедлайн ответа</th>
                                    <th>Статус</th>
                                    <th style={{ width: 200 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {appointments.map(a => {
                                    const status = a.is_responded
                                        ? { color: 'success', label: 'Ответил' }
                                        : (a.is_overdue
                                            ? { color: 'danger', label: 'Просрочен' }
                                            : { color: 'warning', label: 'Ожидает ответа' });
                                    return (
                                        <tr key={a.id} style={{
                                            backgroundColor: a.is_overdue && !a.is_responded ? 'rgba(255,0,0,0.04)' : undefined
                                        }}>
                                            <td>{a.id}</td>
                                            <td>{a.patient_name || patientById(a.patient_id)}</td>
                                            <td>
                                                <Typography level="body-sm">
                                                    {new Date(a.date_appointment).toLocaleString('ru-RU')}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs" color={a.is_overdue && !a.is_responded ? 'danger' : 'neutral'}>
                                                    {new Date(a.deadline_at).toLocaleString('ru-RU')}
                                                    <br />
                                                    <span style={{ opacity: 0.6 }}>лимит {a.response_deadline_hours}ч</span>
                                                </Typography>
                                            </td>
                                            <td><Chip size="sm" variant="soft" color={status.color}>{status.label}</Chip></td>
                                            <td>
                                                {!a.is_responded && (
                                                    <Button
                                                        size="sm"
                                                        variant="soft"
                                                        color="success"
                                                        startDecorator={<DoneAllRounded />}
                                                        onClick={() => handleMarkResponded(a)}
                                                    >
                                                        Отмечен как отвеченный
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Box>
                )}
            </Card>

            <ModalCreateAppointment
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={() => load()}
            />
        </Box>
    );
}
